import { BusyIndicator, Icon } from "client/Components";
import { ReactElement, useEffect, useState } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DestinationWindow } from "client/Api/Apps/common";
import { Guid } from "client_legacy/ndaw/guid";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { ListItem } from "../ListItem";
import { LiveTileContent } from "../LiveTileContent";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { InterestedZenyaDocParty, ZenyaDocLiveInformationProvider } from "./provider";
import {
	deleteFavorite,
	ItemsResultStatus,
	ZenyaDocFavoriteItem,
	ZenyaDocFavoritesResponseModel
} from "client/Api/Workspace/LiveInformation/ZenyaDoc/zenyaDocLiveInformation";
import styles from "./Favorites.module.less";
import { AlertManager, useAlerts, useUnmountedCancellation } from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";

type LoadingState = {
	isLoading: true;
} | (
	{
		isLoading: false;
	}
	&
	(
		{
			hasTooManyConsecutiveErrors: true;
			hasGlobalFailure?: undefined;
			response?: undefined;
		}
		|
		{
			hasGlobalFailure: true;
			hasTooManyConsecutiveErrors?: undefined;
			response?: undefined;
		}
		|
		{
			response: ZenyaDocFavoritesResponseModel;
			hasGlobalFailure?: undefined;
			hasTooManyConsecutiveErrors?: undefined;
		}
	)
);

export type FavoritesProps = {
	tabId: Guid;
	connectionId: number;
	destinationWindow: DestinationWindow;
	provider: ZenyaDocLiveInformationProvider;
	addAnalyticsEvent(): void;
};

export const Favorites = (props: FavoritesProps): ReactElement => {
	const {
		tabId,
		connectionId,
		destinationWindow,
		provider,
		addAnalyticsEvent
	} = props;

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	useEffect(
		() => {
			const party: InterestedZenyaDocParty = {
				partyId: tabId,
				resourceId: {
					connectionId
				},
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					});
				},
				updateFavorites: (response: ZenyaDocFavoritesResponseModel): void => {
					setLoadingState({
						isLoading: false,
						response
					});
				}
			};

			setLoadingState((x) => ({
				...x,
				isLoading: true
			}));

			provider.register(party, { favorites: true});

			return () => {
				provider.unregister(party);
			};
		},
		[
			connectionId,
			provider,
			tabId
		]
	);

	const tileRefreshHandler = (): void => {
		setLoadingState((x) => ({
			...x,
			isLoading: true
		}));
		provider.forceReload();
	};

	const deleteFavoriteHandler = async (id: number): Promise<void> => {

		try {
			await deleteFavorite(
				connectionId,
				id,
				globalCancellation
			);

			setLoadingState((x) => {
				if (!x.isLoading && x.response) {
					return {
						...x,
						response: {
							...x.response,
							items: x.response.items.filter(x => x.id !== id)
						}
					};
				}
				return x;
			});
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			alertManager.showError(localize("_GeneralErrorMessage"));
		}
	};

	const clickHandler = (item: ZenyaDocFavoriteItem): void => {
		addAnalyticsEvent();
		navigateTo(item.url, destinationWindow === DestinationWindow.NewWindow);
	};

	const deleteHandler = (item: ZenyaDocFavoriteItem): void => {
		addAnalyticsEvent();
		deleteFavoriteHandler(item.id);
	};

	const { isAdmin } = userConfiguration();

	const renderContent = (): ReactElement | null => {
		if (loadingState.isLoading) {
			return null;
		}

		if (loadingState.hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={tileRefreshHandler} />;
		}

		if (loadingState.hasGlobalFailure || loadingState.response.status === ItemsResultStatus.GeneralFailure) {
			return <LiveTileContent.Message text={localize("_UnableToRetrieveItems")} />;
		}

		if (loadingState.response.status === ItemsResultStatus.EmailAddressRequired) {
			return (
				<LiveTileContent.Message
					text={localize(isAdmin
						? "_ZenyaEmailRequiredToActivateMessageForAdmin"
						: "_ZenyaEmailRequiredToActivateMessage"
					)}
				/>
			);
		}

		const {
			response
		} = loadingState;

		if (response.items.length === 0) {
			return <LiveTileContent.Message text={localize("_YouDontHaveAnyFavoriteDocuments")} />;
		}

		return (
			<LiveTileContent.List>
				{ response.items.map((x) => (
					<ListItem
						key={x.id}
						title={x.name}
						isHoverable
						hasSeparateClickableAreas
						blockClassName={styles.listItem}
					>
						<ListItem.ClickableArea
							position="first"
							shouldGrow
							onClick={() => clickHandler(x)}
						>
							<ListItem.Content>
								<ListItem.Row>
									<ListItem.Title>
										{x.name}
									</ListItem.Title>
								</ListItem.Row>
							</ListItem.Content>
						</ListItem.ClickableArea>

						<ListItem.ClickableArea
							position="last"
							onClick={() => deleteHandler(x)}
						>
							<div
								className={styles.unfavorIcon}
								title={localize("_RemoveFromFavorites")}
							>
								<Icon value="star-filled" />
							</div>
						</ListItem.ClickableArea>
					</ListItem>
				))}
			</LiveTileContent.List>
		);
	};

	return (
		<LiveTileContent.BusyIndicatorContainer>
			{loadingState.isLoading && (
				<BusyIndicator />
			)}
			<LiveTileContent.Scrollable>
				{renderContent()}
			</LiveTileContent.Scrollable>
		</LiveTileContent.BusyIndicatorContainer>
	);
};
Favorites.displayName = "Favorites";